import { useState } from 'react'
import { toast } from 'sonner'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dropzone } from '@/components/ui/dropzone'
import { AD_HOC_ACCEPTED_FILE_TYPES } from '@/lib/fileUpload'
import { useDropOnVault } from '@/models/vault'
import { useVault } from './VaultContext'
import { type Drop, VaultDropProgress } from './VaultDropProgress'
import { VaultTips } from './VaultTips'
import { REVIEWING_TOAST_ID, ReviewingToast, UNREADABLE_MESSAGE } from './VaultUploadToast'

/** Matches the vault endpoint's own per-file limit, so the two never disagree about a file. */
const MAX_UPLOAD_MB = 25

const ACCEPTED = new Set(
  AD_HOC_ACCEPTED_FILE_TYPES.split(',').map((extension) => extension.trim().toLowerCase())
)

function isAccepted(file: File): boolean {
  const dot = file.name.lastIndexOf('.')
  return dot > 0 && ACCEPTED.has(file.name.slice(dot).toLowerCase())
}

/** The welcome on the active vault: what to do next, and a drop target to do it with. */
export function VaultWelcome({ intakeEmail }: { intakeEmail?: string }) {
  const { orgId } = useVault()
  const drop = useDropOnVault(orgId)
  const [drops, setDrops] = useState<Drop[]>([])

  async function onFiles(files: File[]) {
    const accepted = files.filter(isAccepted)
    if (accepted.length < files.length) toast.error(UNREADABLE_MESSAGE)
    if (accepted.length === 0) return
    const startedAt = Date.now()
    toast.custom(() => <ReviewingToast count={accepted.length} />, {
      id: REVIEWING_TOAST_ID,
      duration: Infinity,
    })
    try {
      const { sourceId } = await drop.mutateAsync(accepted)
      setDrops((prev) => [...prev, { sourceId, startedAt }])
    } catch {
      toast.dismiss(REVIEWING_TOAST_ID)
      toast.error(UNREADABLE_MESSAGE)
    }
  }

  return (
    <Card className="h-full gap-4 py-5">
      <CardHeader>
        <CardTitle className="text-xl">Let&rsquo;s get you audit ready</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Proportional, never max-content: the tip is a paragraph, whose max-content is one long line. */}
        <div className="grid gap-6 sm:grid-cols-2">
          <VaultTips intakeEmail={intakeEmail} />
          {/* Everything reaches `onFiles`: the vault says why a file bounced, not the shared dropzone. */}
          <Dropzone
            accept=""
            maxSizeMB={MAX_UPLOAD_MB}
            showFileHints={false}
            multiple
            isLoading={drop.isPending}
            onMultipleChange={(files) => void onFiles(files)}
          />
        </div>
        {drops.map((item) => (
          <VaultDropProgress
            key={item.sourceId}
            drop={item}
            onDone={() => setDrops((prev) => prev.filter((d) => d.sourceId !== item.sourceId))}
          />
        ))}
      </CardContent>
    </Card>
  )
}
